import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    viewChildren,
} from '@angular/core';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {
    LoadComponent,
    PaginationComponent,
    applySortingToHeaders,
    SortableTableHeaderComponent,
    SortingType,
} from '@brunata-angular/ui';
import {
    caseInsensitiveComparator,
    composeComparators,
    filterNulls,
    FormValue,
    fromControl,
    includesIgnoreCase,
    keyComparator,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {InputDropdownCheckboxesComponent, SelectableItem} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import {uniqBy} from 'lodash-es';
import {BehaviorSubject, combineLatest, map, take, tap} from 'rxjs';
import {EventAlarmsService} from '../event-alarms.service';
import {
    EventAlarmFilterFormGroup,
    EventAlarmWithTranslations,
    VendorModel,
} from '../event-alarm.model';
import {EVENT_ALARM_TABLE_COLUMNS} from './event-alarm-table-column-heads';
import {EventAlarmPipe} from '@shared/pipes/event-alarm.pipe';

@Component({
    selector: 'app-event-alarms-table',
    imports: [
        CommonModule,
        TranslatePipe,
        LoadComponent,
        ReactiveFormsModule,
        SortableTableHeaderComponent,
        PaginationComponent,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        NgbDropdown,
        NgbDropdownItem,
        NgbDropdownMenu,
        NgbDropdownToggle,
        EventAlarmPipe,
    ],
    providers: [EventAlarmPipe],
    templateUrl: './event-alarms-table.component.html',
    styleUrl: './event-alarms-table.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventAlarmsTableComponent implements OnDestroy, OnInit {
    private formBuilder: NonNullableFormBuilder = inject(NonNullableFormBuilder);
    private translateService: TranslateService = inject(TranslateService);
    protected eventAlarmsSubject: BehaviorSubject<EventAlarmWithTranslations[]> =
        new BehaviorSubject<EventAlarmWithTranslations[]>([]);

    private readonly headers = viewChildren(SortableTableHeaderComponent);
    @Input() buildingNo: string | null = null;

    @Output() editAlarm: EventEmitter<string> = new EventEmitter<string>();
    @Output() duplicateAlarm: EventEmitter<string> = new EventEmitter<string>();

    @Input() set eventAlarms(eventAlarms: EventAlarmWithTranslations[]) {
        this.eventAlarmsSubject.next(eventAlarms);
    }

    @Output() deleteEventAlarm: EventEmitter<EventAlarmWithTranslations> =
        new EventEmitter<EventAlarmWithTranslations>();

    formGroupFilter: EventAlarmFilterFormGroup = this.formBuilder.group({
        meterModelCriteriaMismatch: new FormControl<SelectableItem[]>([], {nonNullable: true}),
        eventTypeId: new FormControl<SelectableItem[]>([], {nonNullable: true}),
        vendorModels: new FormControl<SelectableItem[]>([], {nonNullable: true}),
        alarmName: new FormControl<string>('', {nonNullable: true}),
    });

    readonly tableSettings$ = combineLatest({
        sorting: this.eventAlarmsService.sorting.pipe(filterNulls()),
        page: this.eventAlarmsService.page,
        pageSize: this.eventAlarmsService.pageSize,
    });

    readonly tableValues$ = combineLatest([
        fromControl(this.formGroupFilter),
        this.tableSettings$,
        this.eventAlarmsSubject,
    ]).pipe(
        map(([filterControl, settings, eventAlarms]) => {
            const filteredCollection = eventAlarms.filter(eventAlarm =>
                this.eventAlarmFilter(eventAlarm, {
                    meterModelCriteriaMismatch: filterControl.meterModelCriteriaMismatch ?? [],
                    eventTypeId: filterControl.eventTypeId ?? [],
                    vendorModels: filterControl.vendorModels ?? [],
                    alarmName: filterControl.alarmName ?? '',
                }),
            );

            const sortedEventAlarms = filteredCollection.sort(
                this.getColumnComparator(settings.sorting),
            );

            const slicedEventAlarms = sortedEventAlarms.slice(
                (settings.page - 1) * settings.pageSize,
                settings.page * settings.pageSize,
            );
            return {
                settings: settings,
                buildingNo: this.buildingNo,
                page: settings.page,
                pageSize: settings.pageSize,
                eventAlarms: slicedEventAlarms,
                collectionSize: filteredCollection.length,
                noDataFromApi: eventAlarms.length === 0,
            };
        }),
        tap(tableValues =>
            setTimeout(() => {
                applySortingToHeaders(this.headers(), tableValues.settings.sorting);
            }, 0),
        ),
    );

    readonly view$ = this.tableValues$.pipe(
        map(tableValues => ({
            ...tableValues,
            columns: EVENT_ALARM_TABLE_COLUMNS,
        })),
    );

    constructor(
        private eventAlarmsService: EventAlarmsService,
        private eventAlarmPipe: EventAlarmPipe,
    ) {
        this.eventAlarmsService.filterSubject.pipe(take(1)).subscribe(filterData => {
            this.formGroupFilter.patchValue({
                meterModelCriteriaMismatch: filterData.meterModelCriteriaMismatch ?? [],
                vendorModels: filterData.type ?? [],
                eventTypeId: filterData.eventTypeId ?? [],
                alarmName: filterData.alarmName ?? '',
            });
        });
    }

    ngOnInit(): void {
        this.formGroupFilter.patchValue({
            vendorModels: this.getUniqueValuesForLists(this.eventAlarmsSubject.value, 'type'),
            eventTypeId: this.getUniqueValuesForLists(this.eventAlarmsSubject.value, 'eventTypeId'),
            meterModelCriteriaMismatch: this.getUniqueValuesForLists(
                this.eventAlarmsSubject.value,
                'meterModelCriteriaMismatch',
            ),
        });
    }

    ngOnDestroy(): void {
        this.eventAlarmsService.setFilterColumns({
            alarmName: this.formGroupFilter.value.alarmName ?? '',
            meterModelCriteriaMismatch: this.formGroupFilter.value.meterModelCriteriaMismatch ?? [],
            eventTypeId: this.formGroupFilter.value.eventTypeId ?? [],
            type: this.formGroupFilter.value.vendorModels ?? [],
        });
    }

    setPage(page: number): void {
        this.eventAlarmsService.page.next(page);
    }

    setPageSize(pageSize: number): void {
        this.eventAlarmsService.pageSize.next(pageSize);
    }

    onSort(sorting: SortingType<EventAlarmWithTranslations>): void {
        applySortingToHeaders(this.headers(), sorting);
        this.eventAlarmsService.updateSorting(sorting);
    }

    private getUniqueValuesForLists(
        eventAlarms: EventAlarmWithTranslations[],
        key: 'type' | 'eventTypeId' | 'meterModelCriteriaMismatch',
    ): SelectableItem[] {
        const valueList: SelectableItem[] = eventAlarms.flatMap(agent => {
            switch (key) {
                case 'type':
                    return agent.vendorModels.map(it => ({
                        name: this.getMeterTypeTranslationAndName(it),
                        selected: false,
                    }));
                case 'eventTypeId':
                    return {
                        name: this.eventAlarmPipe.transform(agent.eventTypeId),
                        selected: false,
                    };
                case 'meterModelCriteriaMismatch':
                    return {
                        name: agent.meterModelCriteriaMismatch
                            ? this.translateService.translate(
                                  'event-alarms_unsupportedEventTypeMsg',
                              )
                            : this.translateService.translate(
                                  'common_All_connected_devices_supported',
                              ),
                        selected: false,
                    };
            }
        });

        return uniqBy(valueList, item => item.name).sort(
            composeComparators([
                keyComparator(item => item.sortOrder),
                keyComparator(item => item.name),
            ]),
        );
    }

    private eventAlarmFilter(
        eventAlarm: EventAlarmWithTranslations,
        filter: FormValue<EventAlarmFilterFormGroup>,
    ): boolean {
        const meterModelCriteriaMismatch = this.filterEventAlarmFromList(
            eventAlarm.meterModelCriteriaMismatch,
            filter.meterModelCriteriaMismatch,
            'meterModelCriteriaMismatch',
        );
        const eventTypeId = this.filterEventAlarmFromList(
            eventAlarm.eventTypeId,
            filter.eventTypeId,
            'eventTypeId',
        );

        const meterType = eventAlarm.vendorModels.some(vendorModel =>
            this.filterEventAlarmFromList(
                this.getMeterTypeTranslationAndName(vendorModel).toLowerCase(),
                filter.vendorModels,
            ),
        );

        const alarmNameMatch = includesIgnoreCase(eventAlarm.alarmName, filter.alarmName);

        return meterModelCriteriaMismatch && eventTypeId && meterType && alarmNameMatch;
    }

    private filterEventAlarmFromList(
        value: string | number | boolean | null,
        selectableItem: SelectableItem[],
        parameter?: string,
    ): boolean {
        const valuesToFilter = selectableItem
            .filter(it => it.selected)
            .map(it => it.name.toString().toLowerCase());
        if (valuesToFilter.length === 0 || value == null || selectableItem.length === 0) {
            return true;
        }

        switch (parameter) {
            case 'meterModelCriteriaMismatch':
                return valuesToFilter.includes(
                    this.translateService
                        .translate(
                            value === false
                                ? 'common_All_connected_devices_supported'
                                : 'event-alarms_unsupportedEventTypeMsg',
                        )
                        .toLowerCase(),
                );
            case 'eventTypeId': {
                return valuesToFilter.includes(
                    this.eventAlarmPipe.transform(value.toString()).toLowerCase(),
                );
            }
            default:
                return valuesToFilter.includes(value.toString());
        }
    }

    private getColumnComparator({
        column,
        direction,
    }: SortingType<EventAlarmWithTranslations>): (
        a: EventAlarmWithTranslations,
        b: EventAlarmWithTranslations,
    ) => number {
        switch (column) {
            case 'meterModelCriteriaMismatch':
            case 'alarmName':
            case 'vendorModels':
                return caseInsensitiveComparator(
                    eventAlarm => eventAlarm[column].toString(),
                    direction,
                );
            case 'eventTypeId':
                return keyComparator(
                    eventAlarm => this.eventAlarmPipe.transform(eventAlarm.eventTypeId),
                    direction,
                );

            default:
                return keyComparator(eventAlarm => eventAlarm[column].toString(), direction);
        }
    }

    protected getMeterTypeTranslationAndName(meterType: VendorModel): string {
        return `${this.translateService.translate(
            'mappers_meterType.' + meterType.meterType.toString(),
            'online',
            'common',
        )} - ${meterType.modelName}`;
    }
}
